import { getDocumentListPromise } from "app/api/documentsApi";
import { VerificationDocumentsHelper } from "app/helpers/verificationDocumentsHelper";
import store from "app/store/store";
import type { VerificationDocumentType } from "app/types/documentType";
import { DocumentCategory, DocumentsForUpload } from "app/utils/const";
import React, { useEffect, useState } from "react";
import { injectIntl, IntlShape } from "react-intl";
import { connect } from "react-redux";
import { PulseLoader } from "react-spinners";
import CategorySelection from "../CategorySelection/CategorySelection";
import DocumentUploadForm from "../DocumentUploadForm/DocumentUploadForm";
import IdentityDocumentChoice from "../IdentityDocumentChoice/IdentityDocumentChoice";
import StartVerification from "../StartVerification/StartVerification";
import "./VerifyYourIdentity.scss";

enum VerificationStep {
  CategorySelection,
  IdentityDocumentChoice,
  DocumentUploadFrontSide,
  DocumentUploadBackSide,
}

type Props = {
  intl: IntlShape;
  documentList: VerificationDocumentType[];
};
const VerifyYourIdentity: React.FC<Props> = props => {
  const { intl, documentList } = props;
  const [currentStep, setCurrentStep] = useState<VerificationStep>(
    VerificationStep.CategorySelection
  );

  // selectedDocumentCategory can only be IDENTITY or PROOFOFADDRESS
  const [selectedDocumentCategory, setSelectedDocumentCategory] = useState<
    (typeof DocumentCategory)[keyof typeof DocumentCategory]
  >(DocumentCategory.IDENTITY);

  // selectedIdentityDocumentCategory can only be PASSPORT, DRIVING_LICENCE, ID_CARD_FRONT, ID_CARD_BACK, POA
  const [
    selectedIdentityDocumentCategory,
    setSelectedIdentityDocumentCategory,
  ] =
    useState<(typeof DocumentsForUpload)[keyof typeof DocumentsForUpload]>("");

  const [isLoading, setIsLoading] = useState(true);

  const handleCategorySelect = (
    category: (typeof DocumentCategory)[keyof typeof DocumentCategory] // ie "Identity" | "Proof of Address"
  ) => {
    setSelectedDocumentCategory(category);
    // if idcardFront already uploaded then load back
    // if back load the front
    const frontOrBack =
      VerificationDocumentsHelper.checkIfFrontOrBackOnlyUploaded(documentList);

    if (frontOrBack === DocumentsForUpload.ID_CARD_FRONT) {
      setCurrentStep(VerificationStep.DocumentUploadBackSide);
      setSelectedIdentityDocumentCategory(DocumentsForUpload.ID_CARD_BACK);
    } else if (frontOrBack === DocumentsForUpload.ID_CARD_BACK) {
      setCurrentStep(VerificationStep.DocumentUploadFrontSide);
      setSelectedIdentityDocumentCategory(DocumentsForUpload.ID_CARD_FRONT);
    } else {
      setCurrentStep(
        category === DocumentCategory.IDENTITY
          ? VerificationStep.IdentityDocumentChoice
          : VerificationStep.DocumentUploadFrontSide
      );
      category === DocumentCategory.PROOFOFADDRESS &&
        setSelectedIdentityDocumentCategory(DocumentsForUpload.POA);
    }
  };

  const handleIdentityDocumentChoice = (category: string) => {
    setSelectedIdentityDocumentCategory(category);
    setCurrentStep(VerificationStep.DocumentUploadFrontSide);
  };

  const handleDocumentUploadGoBack = () => {
    setCurrentStep(VerificationStep.CategorySelection);
  };

  const handleDocumentUpload = () => {
    // Go to first step and show the newly uploaded file
    // if it is idcard, show the idcardback step, unless both idcard front and back are already uploaded,

    const frontOrBack =
      VerificationDocumentsHelper.checkIfFrontOrBackOnlyUploaded(documentList);

    if (
      selectedIdentityDocumentCategory === DocumentsForUpload.ID_CARD_FRONT &&
      !frontOrBack
    ) {
      setCurrentStep(VerificationStep.DocumentUploadBackSide);
      setSelectedIdentityDocumentCategory(DocumentsForUpload.ID_CARD_BACK);
      renderDocumentUpload();
    } else {
      getDocuments();
      setCurrentStep(VerificationStep.CategorySelection);
    }
  };

  useEffect(() => {
    getDocuments();
  }, []);

  const getDocuments = async () => {
    try {
      setIsLoading(true);
      const docs = await getDocumentListPromise();
      store.dispatch.documentListModel.updateDocumentList({
        documentList: docs,
      });
      // If id documents uploaded then show Proof of Address
      VerificationDocumentsHelper.verifyIdUploaded(docs, intl) === ""
        ? setSelectedDocumentCategory(DocumentCategory.PROOFOFADDRESS)
        : setSelectedDocumentCategory(DocumentCategory.IDENTITY);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderIdentityDocumentChoice = () => (
    <IdentityDocumentChoice
      onDocumentSelect={handleIdentityDocumentChoice}
      onBackClick={() => setCurrentStep(VerificationStep.CategorySelection)}
    />
  );

  const renderDocumentUpload = () => {
    return (
      <DocumentUploadForm
        selectedDocumentCategory={selectedDocumentCategory}
        selectedIdentityDocumentCategory={selectedIdentityDocumentCategory}
        onBackClick={handleDocumentUploadGoBack}
        onDocumentUpload={handleDocumentUpload}
      />
    );
  };
  return (
    <div>
      {isLoading && (
        <>
          <div className="account-verification__content--loading-spinner">
            <PulseLoader size={20} />
          </div>
        </>
      )}
      <div
        className={`account-verification__content ${
          isLoading ? "account-verification__content--fade" : ""
        }`}
      >
        {currentStep === VerificationStep.CategorySelection && (
          <CategorySelection
            selectedDocumentCategory={selectedDocumentCategory}
            documentList={documentList}
            handleCategorySelect={handleCategorySelect}
            getDocuments={getDocuments}
            intl={intl}
          />
        )}
        {currentStep === VerificationStep.IdentityDocumentChoice &&
          renderIdentityDocumentChoice()}
        {currentStep === VerificationStep.DocumentUploadFrontSide &&
          renderDocumentUpload()}
        {currentStep === VerificationStep.DocumentUploadBackSide &&
          renderDocumentUpload()}
        {currentStep === VerificationStep.CategorySelection && (
          <StartVerification
            documentList={documentList}
            intl={intl}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};
const mapStateToProps = state => ({
  documentList: state.documentListModel.documentList,
});

const mapDispatchToProps = (dispatch: any) => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(VerifyYourIdentity)) as any;
